import './usdt-withdrawal-count.scss';
import { FC, useEffect, useState } from 'react';
import { Card } from '../../../../../../components/card/card';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Exporting from 'highcharts/modules/exporting';
import ExportData from 'highcharts/modules/export-data';
import { toJalaliDateOrNull } from 'core/utils/date';
import { USDTWithdrawalCountChartProps } from '../../../../../../core/domain/overview/usdt-withdrawal-count';

Exporting(Highcharts);
ExportData(Highcharts);

interface Props {
    chartData: USDTWithdrawalCountChartProps;
    chartLoading: boolean;
}

interface FormattedData {
    dateTime: string;
    value: number;
}

export const USDTWithdrawalCount: FC<Props> = ({ chartData, chartLoading }) => {
    const [options, setOptions] = useState<Highcharts.Options>({});

    useEffect(() => {
        const formatData = (withdrawalUsdtChart: { dateTime: string; value: number }[]): FormattedData[] => {
            const formattedData: { [key: string]: FormattedData } = {};

            withdrawalUsdtChart.forEach((item) => {
                const date = toJalaliDateOrNull(item.dateTime);
                if (!date) return; // skip invalid/sentinel dates
                if (!formattedData[date]) {
                    formattedData[date] = { dateTime: date, value: item.value };
                }
                formattedData[date].value = item.value;
            });

            return Object.values(formattedData);
        };

        if (chartData) {
            const combinedData = formatData(chartData.withdrawalUsdtChart);
            const dates = combinedData.map((item) => item.dateTime);
            const values = combinedData.map((item) => item.value);

            setOptions({
                chart: {
                    style: {
                        fontFamily: 'YekanBakh',
                        fontSize: '16px',
                    },
                },
                lang: {
                    thousandsSep: ',',
                    downloadPNG: 'PNG دانلود',
                    downloadJPEG: 'JPEG دانلود',
                    downloadPDF: 'PDF دانلود',
                    downloadSVG: 'SVG دانلود',
                    downloadCSV: 'CSV دانلود',
                    downloadXLS: 'XLS دانلود',
                },
                tooltip: {
                    style: {
                        fontSize: '16px',
                    },
                    formatter: function () {
                        return (
                            '<b>' +
                            this.x +
                            '</b><br>' +
                            '<span style="color:' +
                            this.series.color +
                            '"><span>' +
                            this.series.name +
                            '</span>: ' +
                            '<b>' +
                            Highcharts.numberFormat(this.y, 0, '.', ',') +
                            '</b>' +
                            '</span>'
                        );
                    },
                },
                title: {
                    text: 'تعداد برداشت های تتر',
                },
                xAxis: {
                    categories: dates,
                },
                yAxis: {
                    title: {
                        text: 'مقدار',
                    },
                    labels: {
                        formatter: function () {
                            const value = this.value as number;
                            if (value >= 1_000_000) {
                                return value / 1_000_000 + ' میلیون';
                            } else if (value >= 1_000) {
                                return value / 1_000 + ' هزار';
                            } else {
                                return value.toString();
                            }
                        },
                        style: {
                            fontFamily: 'YekanBakh',
                            direction: 'rtl',
                        },
                    },
                },
                series: [
                    {
                        name: 'برداشت',
                        data: values,
                        type: 'spline',
                        color: '#4CAF50',
                    },
                ],
                exporting: {
                    enabled: true,
                    buttons: {
                        contextButton: {
                            menuItems: [
                                'downloadPNG',
                                'downloadJPEG',
                                'downloadPDF',
                                'downloadSVG',
                                'downloadCSV',
                                'downloadXLS',
                            ],
                        },
                    },
                },
                credits: {
                    enabled: false,
                },
            });
        }
    }, [chartData]);

    return (
        <Card title="تعداد برداشت های تتر" blur={chartLoading} className={'pmx-usdt-withdrawal-count'}>
            {chartData && <HighchartsReact highcharts={Highcharts} options={options} />}
        </Card>
    );
};
